import { useEffect } from 'react';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import { ShieldCheck, Mail } from 'lucide-react';
import '../Policy.css';

export default function Terms() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="policy-page">
      <Navbar />
      <main className="policy-page__main">
        <header className="policy-header">
          <div className="policy-kicker">Legal & Compliance</div>
          <h1 className="policy-title">Terms of Service</h1>
          <p className="policy-subtitle">
            Please read these terms carefully before using Revitrone's software, plugins, and AI proxy services.
          </p>
          <div className="policy-meta">
            <ShieldCheck size={14} />
            <span>Effective Date: August 25, 2026</span>
          </div>
        </header>

        <div className="policy-card">
          <section className="policy-section">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing or using the Revitrone application, downloading the Revitrone Revit plugin, creating an account, or interacting with our Managed AI Proxy APIs (collectively, the "Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to all of these Terms, do not access or use our Service.
            </p>
          </section>

          <section className="policy-section">
            <h2>2. Description of Service</h2>
            <p>
              Revitrone provides artificial intelligence tools, Revit plugin integrations, Dynamo/Python scripting support, and managed API proxy services tailored for architectural design, modeling, and automation workflows.
            </p>
          </section>

          <section className="policy-section">
            <h2>3. Account Registration & Device Limits</h2>
            <p>
              To access certain features of the Service, you must register for an account using valid credentials. You are responsible for maintaining the confidentiality of your credentials and for all activities occurring under your account.
            </p>
            <ul>
              <li><strong>Device Login Restrictions:</strong> Depending on your subscription plan, your account access is restricted to a maximum number of concurrent device logins (e.g., Basic: 1 device, Pro: 2 devices, Studio: 5 devices, Enterprise: 10+ seats).</li>
              <li><strong>Account Sharing:</strong> Credential sharing beyond your plan's seat and device limit is strictly prohibited and may result in temporary suspension or termination.</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>4. API Usage & Subscription Models</h2>
            <h3>A. Bring Your Own API Key (BYOK)</h3>
            <p>
              Under our Basic plan, users may supply their own third-party API keys (e.g., OpenAI, Anthropic). You remain solely responsible for any fees, rate limits, and compliance obligations associated with your third-party API provider accounts.
            </p>
            <h3>B. Managed API Proxy & Token Allocations</h3>
            <p>
              Paid plans (Pro, Studio, Enterprise) include pre-allocated token allowances routed through Revitrone's secure API proxy. Token consumption is tracked in real-time. Monthly token allocations reset at the beginning of each billing cycle and do not roll over to subsequent months.
            </p>
          </section>

          <div className="policy-callout">
            <p>
              <strong>Important Architectural Disclaimer:</strong> Revitrone tools and AI-generated outputs are intended solely for conceptual acceleration, modeling assistance, and administrative workflow efficiency. Outputs generated by Revitrone do not constitute certified structural engineering, code-compliant blueprints, or stamped architectural documents. All designs must be reviewed, verified, and stamped by a licensed professional architect or structural engineer prior to construction or municipal submission.
            </p>
          </div>

          <section className="policy-section">
            <h2>5. Intellectual Property & Data Ownership</h2>
            <ul>
              <li><strong>Your Content & Models:</strong> You retain complete ownership of all building information models (BIM), CAD files, scripts, designs, and project data you upload or process through Revitrone.</li>
              <li><strong>Revitrone IP:</strong> Revitrone retains all rights, title, and interest in and to the Revitrone software, plugin source code, branding, interface designs, and proprietary proxy infrastructure.</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>6. Payments & Billing</h2>
            <p>
              All subscription purchases, transactions, and invoicing are processed by our authorized Merchant of Record, <strong>Paddle.com Market Ltd ("Paddle")</strong>. By subscribing to a paid plan, you authorize Paddle to bill your designated payment method on a recurring monthly or annual basis.
            </p>
          </section>

          <section className="policy-section">
            <h2>7. Prohibited Conduct</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Reverse engineer, decompile, or attempt to extract source code from the Revitrone plugin or proxy.</li>
              <li>Attempt to bypass device login limits, proxy rate limits, or authentication mechanisms.</li>
              <li>Use the Service to generate malicious code, spam, or unlawful architectural submittals.</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>8. Termination</h2>
            <p>
              We reserve the right to suspend or terminate your access to the Service at our discretion, without prior notice, if you breach these Terms or engage in fraudulent or abusive activities.
            </p>
          </section>

          <section className="policy-section">
            <h2>9. Changes to Terms</h2>
            <p>
              We may modify these Terms at any time. Continued use of the Service after changes become effective constitutes acceptance of the revised Terms.
            </p>
          </section>

          <div className="policy-contact-box">
            <h3>Have Questions About Our Terms?</h3>
            <p>If you have any questions or require clarification regarding our Terms of Service, please contact our legal team.</p>
            <a href="mailto:revitroneapp@gmail.com" className="policy-contact-btn">
              <Mail size={16} /> Contact Legal Support
            </a>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
